import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { shared } from '../styles/shared.js';
import { LocaleController } from '../i18n/locale-controller.js';
import { t } from '../i18n/t.js';

/**
 * Fullscreen overlay shown when the device is held in portrait. Displayed via
 * a CSS media query on this element's host so it appears whenever the
 * viewport orientation changes — covering both web and PWA install cases.
 *
 * No audio. The toddler shouldn't hear feedback for "wrong" orientation.
 */
@customElement('rotate-overlay')
export class RotateOverlay extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        display: none;
      }

      /*
       * Host (not just inner content) gets fullscreen layout in portrait so
       * the element has non-zero size — important for visibility detection
       * by both screen readers and E2E tooling.
       */
      @media (orientation: portrait) {
        :host {
          position: fixed;
          inset: 0;
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: var(--space-4);
          padding: var(--space-6);
          text-align: center;
          background: var(--color-bg);
        }
      }

      .icon {
        font-size: 96px;
        line-height: 1;
        animation: nudge 1.6s var(--easing-standard) infinite;
      }

      @keyframes nudge {
        0%,
        100% {
          transform: rotate(0deg);
        }
        50% {
          transform: rotate(-90deg);
        }
      }
    `,
  ];

  private locale = new LocaleController(this);

  override render() {
    return html`<span class="icon" aria-hidden="true">📱</span>
      <h1 class="text-display">${t(this.locale.locale, 'rotate.title')}</h1>
      <p class="text-body">${t(this.locale.locale, 'rotate.body')}</p>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rotate-overlay': RotateOverlay;
  }
}
